// src/admin/components/AdminSidebar.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  HelpCircle,
  CheckSquare,
  PenTool,
  X,
  LogOut,
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import logoImg from "../../assets/navbar-logo.png";

const navItems = [
  {
    name: "Dashboard",
    path: "/admin/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Daily Current Affairs",
    path: "/admin/daily-ca",
    icon: FileText,
  },
  {
    name: "Monthly Current Affairs",
    path: "/admin/monthly-ca",
    icon: Calendar,
  },
  {
    name: "Question Bank",
    path: "/admin/question-bank",
    icon: HelpCircle,
  },
  {
    name: "Prelims Initiative",
    path: "/admin/prelims",
    icon: CheckSquare,
  },
  {
    name: "Mains Initiative",
    path: "/admin/mains",
    icon: PenTool,
  },
];

const AdminSidebar = ({ isMobileOpen, setIsMobileOpen }) => {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();

  const handleLogout = () => {
    setIsMobileOpen(false);
    logout();
    navigate("/");
  };
  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar (Fixed 230px Width) */}
      <aside
        className={`fixed top-0 bottom-0 left-0 z-50 w-[230px] flex flex-col bg-[#0f172a] text-slate-300 border-r border-slate-800 transition-transform duration-200 ease-in-out
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Academy Logo Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 bg-[#0b1120]">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="w-8 h-8 rounded-lg bg-white p-1 flex-shrink-0 flex items-center justify-center shadow-sm">
              <img
                src={logoImg}
                alt="Selva's IAS Academy"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <span className="block font-bold text-xs text-white tracking-wide truncate">
                SELVA'S <span className="text-amber-400">IAS</span>
              </span>
              <span className="block text-[10px] text-slate-400 font-medium tracking-wider uppercase truncate">
                Admin Panel
              </span>
            </div>
          </div>

          {/* Mobile Close Button */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="md:hidden text-slate-400 hover:text-white p-1 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors
                  ${
                    isActive
                      ? "bg-red-900 text-white font-semibold shadow-sm"
                      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
                  }
                `}
              >
                <Icon size={16} className="flex-shrink-0 opacity-90" />
                <span className="truncate">{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom Logout Item */}
        <div className="p-2.5 border-t border-slate-800 bg-[#0b1120]/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-rose-400 hover:bg-rose-950/40 hover:text-rose-300 transition-colors"
          >
            <LogOut size={16} className="text-rose-400 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
